import { adApiRequest, normaliseBrandButton, type AdBrand, type BrandButton, type Palette } from './banner-kit-api';
import { jsonEqual } from './playable-api';

/**
 * The brand's website theme, as spark-creatives reads it from the deployed site (`pkg/brandstyle`, the same source
 * the video ads' brand styling uses): the palette, the call-to-action button and the fonts. A new image banner,
 * HTML5 banner or mini-game starts from it so every ad looks like the brand's own site, and the mini-game composer
 * can reset its colours back to it. Wire shape: `GET /creatives/tenant/{tenant_id}/brand-theme`.
 */

/** The brand theme. `source` is '' (and every field empty) when the site has no readable theme. */
export interface BrandTheme {
  palette?: Palette;
  button?: BrandButton;
  font_heading?: string;
  font_body?: string;
  source: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const str = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** A brand theme from a wire value (bare or wrapped in `data`), keeping only the fields it actually fills; null when
 *  it fills nothing (no readable theme). Pure. */
export const normaliseBrandTheme = (raw: unknown): BrandTheme | null => {
  const outer = isRecord(raw) ? raw : {};
  const d = isRecord(outer.data) ? outer.data : outer;
  const p = isRecord(d.palette) ? d.palette : {};
  const palette: Palette = { primary: str(p.primary), accent: str(p.accent), bg: str(p.bg), text: str(p.text) };
  const hasPalette = Object.values(palette).some(Boolean);
  const button = normaliseBrandButton(d.button);
  const fontHeading = str(d.font_heading);
  const fontBody = str(d.font_body);
  if (!hasPalette && !button && !fontHeading && !fontBody) return null;
  return {
    ...(hasPalette ? { palette } : {}),
    ...(button ? { button } : {}),
    ...(fontHeading ? { font_heading: fontHeading } : {}),
    ...(fontBody ? { font_body: fontBody } : {}),
    source: str(d.source),
  };
};

/** A palette with no colour set (each empty colour is the template's own). */
export const EMPTY_PALETTE: Palette = { primary: '', accent: '', bg: '', text: '' };

/** The brand with the theme's palette, button and fonts over it. A theme field left empty keeps the brand's own (a
 *  blank palette colour keeps the current one), so a partial theme never blanks what is there. Pure. */
export const applyBrandTheme = (brand: AdBrand, theme: BrandTheme | null | undefined): AdBrand => {
  if (!theme) return brand;
  const cur = brand.palette;
  const p = theme.palette;
  return {
    ...brand,
    ...(p
      ? {
          palette: {
            primary: p.primary || cur?.primary || '',
            accent: p.accent || cur?.accent || '',
            bg: p.bg || cur?.bg || '',
            text: p.text || cur?.text || '',
          },
        }
      : {}),
    ...(theme.button ? { button: { ...theme.button } } : {}),
    ...(theme.font_heading ? { font_heading: theme.font_heading } : {}),
    ...(theme.font_body ? { font_body: theme.font_body } : {}),
  };
};

/** Whether the brand already carries the theme (applying it would change nothing). Pure. */
export const brandMatchesTheme = (brand: AdBrand, theme: BrandTheme | null | undefined): boolean =>
  !theme || jsonEqual(applyBrandTheme(brand, theme), brand);

/** How long a create waits for the theme before it goes ahead without it. A network bound, not an animation timing. */
const THEME_TIMEOUT_MS = 4000;

// The theme per tenant, fetched once per page load and shared. A brand with no theme (404, or an empty answer) is
// cached as null; a request that failed outright (network, timeout, 5xx) is dropped so the next caller retries.
const themeRequests = new Map<number, Promise<BrandTheme | null>>();

/** The brand's website theme, or null when it has none or it could not be read. Never throws; never waits longer than
 *  a few seconds (a create awaits it). */
export const fetchBrandTheme = (tenantId: number): Promise<BrandTheme | null> => {
  const cached = themeRequests.get(tenantId);
  if (cached) return cached;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), THEME_TIMEOUT_MS);
  const request = adApiRequest<Record<string, unknown>>(
    'get',
    `/creatives/tenant/${encodeURIComponent(String(tenantId))}/brand-theme`,
    undefined,
    controller.signal,
  )
    .then((res) => {
      if (res.ok) return normaliseBrandTheme(res.data);
      // 404: this backend has no theme for the brand (or no endpoint yet): cache "none". Anything else: retry later.
      if (res.status !== 404) themeRequests.delete(tenantId);
      return null;
    })
    .finally(() => clearTimeout(timer));
  themeRequests.set(tenantId, request);
  return request;
};
